import { useEffect, useState, Fragment, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  User,
  Mail,
  Building,
  Phone,
  Save,
  Loader2,
  X,
  DollarSign,
  AlertCircle,
  Globe,
  Globe2,
  AtSign,
  Tag,
} from "lucide-react";
import { SearchableSelect, InputCustom, DateTimePicker } from "../../components";
import leadsService from "../../services/leadsService";

// "YYYY-MM-DDTHH:MM" in the user's local timezone — the value format <input type="datetime-local"> expects.
const nowDatetimeLocal = () => {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 16);
};

const toDatetimeLocal = (raw) => {
  if (!raw) return nowDatetimeLocal();
  const d = new Date(raw);
  if (isNaN(d.getTime())) return nowDatetimeLocal();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 16);
};

// ---------- Static options (avoid recreating each render) ----------
const PLATFORM_OPTIONS_RAW = [
  { id: 1, name: "Meta", value: "Meta" },
  { id: 2, name: "Google", value: "Google" },
  { id: 3, name: "TikTok", value: "TikTok" },
  { id: 4, name: "Snapchat", value: "Snapchat" },
];

const MONTHLY_SPEND_OPTIONS_RAW = [
  { id: 1, name: "$0 - $50K", value: "$0 - $50K" },
  { id: 2, name: "$50K - $100K", value: "$50K - $100K" },
  { id: 3, name: "$100K - $200K", value: "$100K - $200K" },
  { id: 4, name: "$200K - $300K", value: "$200K - $300K" },
  { id: 5, name: "$300K - $400K", value: "$300K - $400K" },
  { id: 6, name: "$400K - $500K", value: "$400K - $500K" },
  { id: 7, name: "$500K - $1M", value: "$500K - $1M" },
  { id: 8, name: "$1M+", value: "$1M+" },
];

const CATEGORY_OPTIONS_RAW = [
  { id: 1, name: "White Hat (Compliant Ads)", value: "White Hat (Compliant Ads)" },
  { id: 2, name: "Grey Hat (High-Risk Ads)", value: "Grey Hat (High-Risk Ads)" },
  { id: 3, name: "Black Hat (Non-Compliant Ads)", value: "Black Hat (Non-Compliant Ads)" },
  { id: 4, name: "Other", value: "Other" },
];

const KNOWN_CATEGORIES = [
  "White Hat (Compliant Ads)",
  "Grey Hat (High-Risk Ads)",
  "Black Hat (Non-Compliant Ads)",
];

const STATUS_OPTIONS_RAW = [
  { id: 1, name: "All Statuses", value: "" },
  { id: 2, name: "Incoming", value: "Incoming" },
  { id: 2, name: "Unreachable", value: "Unreachable" },

  { id: 3, name: "Engaged", value: "Engaged" },
  { id: 4, name: "Pending", value: "Pending" },
  { id: 5, name: "In Negotiation", value: "In Negotiation" },
  { id: 6, name: "Ready to Start", value: "Ready to Start" },
  { id: 7, name: "Onboarded", value: "Onboarded" },
  { id: 8, name: "Declined", value: "Declined" },
]


// ---------- Helper UI ----------
const ErrorText = ({ children }) =>
  children ? (
    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
      <AlertCircle size={12} />
      {children}
    </p>
  ) : null;

const LeadFormModal = ({ isOpen, onClose, leadId = null, onSuccess }) => {
  const { t } = useTranslation();

  // Map/translate option labels at render time (keeps i18n clean)
  const platformOptions = PLATFORM_OPTIONS_RAW.map((o) => ({ ...o, name: t(o.name) }));
  const categoryOptions = CATEGORY_OPTIONS_RAW.map((o) => ({ ...o, name: t(o.name) }));
  const baseMonthlySpendOptions = MONTHLY_SPEND_OPTIONS_RAW.map((o) => ({ ...o, name: t(o.name) }));
  const statusOptions = STATUS_OPTIONS_RAW.map((o) => ({ ...o, name: t(o.name) }));

  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Form data (normalized defaults)
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    platform: "Meta",          // normalized to proper label
    monthlySpend: "",          // force explicit selection
    companyName: "",
    phone: "",
    status: "Incoming",
    notes: "",
    store: "",
    category: "",
    telegram: "",
    joinDate: nowDatetimeLocal(),
  });

  const [errors, setErrors] = useState({});

  // Category dropdown state (separate from formData.category to support "Other")
  const [categoryChoice, setCategoryChoice] = useState("");
  const [customCategory, setCustomCategory] = useState("");

  const resetForNewLead = useCallback(() => {
    setFormData({
      fullName: "",
      email: "",
      platform: "Meta",
      monthlySpend: "",
      companyName: "",
      phone: "",
      status: "Incoming",
      notes: "",
      store: "",
      category: "",
      telegram: "",
      joinDate: nowDatetimeLocal(),
    });
    setCategoryChoice("");
    setCustomCategory("");
    setErrors({});
  }, []);

  const getLeadToUpdate = useCallback(async () => {
    try {
      const response = await leadsService.get(leadId);
      const data = response.data?.data;

      if (response.data?.statusCode === 200 && data) {
        setFormData({
          fullName: data.name || "",
          email: data.email || "",
          platform: data.platform || "Meta",
          monthlySpend: data.monthlySpend || "",
          companyName: data.company || "",
          phone: data.phone || "",
          status: data.status || "Incoming",
          notes: data.notes || "",
          store: data.store || "",
          category: data.category || "",
          telegram: data.telegram || "",
          joinDate: toDatetimeLocal(data.joinDate),
        });

        const cat = data.category || "";
        if (cat && !KNOWN_CATEGORIES.includes(cat)) {
          setCategoryChoice("Other");
          setCustomCategory(cat);
        } else {
          setCategoryChoice(cat);
          setCustomCategory("");
        }
      }
    } catch (error) {
      console.error("Lead fetch failed:", error.response?.data);
      toast.error(t("Failed to load lead data"));
    }
  }, [leadId]);

  useEffect(() => {
    if (!isOpen) return;
    const run = async () => {
      try {
        setLoading(true);
        if (leadId) {
          await getLeadToUpdate();
        } else {
          resetForNewLead();
        }
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [isOpen, leadId, getLeadToUpdate, resetForNewLead]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Category dropdown handlers
  const handleCategoryChoice = (v) => {
    setCategoryChoice(v);
    if (v === "Other") {
      handleChange("category", customCategory);
    } else {
      setCustomCategory("");
      handleChange("category", v);
    }
  };

  const handleCustomCategoryChange = (v) => {
    setCustomCategory(v);
    handleChange("category", v);
  };

  const validateForm = () => {
    const e = {};
    const v = (s) => String(s ?? "").trim();

    if (!v(formData.fullName)) e.fullName = t("Full name is required");
    if (!v(formData.email)) e.email = t("Email is required");
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = t("Email is invalid");

    if (!v(formData.platform)) e.platform = t("Platform is required");
    if (!v(formData.category)) e.category = t("Category is required");
    if (!v(formData.monthlySpend)) e.monthlySpend = t("Monthly spend is required");
    if (!v(formData.companyName)) e.companyName = t("Company name is required");
    if (!v(formData.phone)) e.phone = t("Phone number is required");

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setProcessing(true);
      const payload = {
        // normalize backend field names if needed
        fullName: formData.fullName,
        email: formData.email,
        platform: formData.platform,
        monthlySpend: formData.monthlySpend,
        companyName: formData.companyName,
        phone: formData.phone,
        status: formData.status,
        notes: formData.notes,
        store: formData.store,
        category: formData.category,
        telegram: formData.telegram,
        joinDate: formData.joinDate,
      };

      const res = leadId
        ? await leadsService.update(leadId, payload)
        : await leadsService.create(payload);

      if (res.data?.statusCode === 200 || res.data?.statusCode === 201) {
        toast.success(leadId ? t("Lead updated successfully") : t("Lead created successfully"));
        onSuccess && onSuccess();
        onClose();
      } else {
        throw new Error(res.data?.message || "Unknown error");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t("An error occurred"));
      console.error("Lead operation failed:", error.response?.data || error);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-xl transition-all">
                {/* Header */}
                <div className="border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                      <User className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
                      {leadId ? t("Update Lead") : t("Add New Lead")}
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 size={30} className="animate-spin text-neutral-600 dark:text-neutral-400" />
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Full Name */}
                        <div className="space-y-1">
                          <InputCustom
                            label={t("Full Name")}
                            placeholder={t("Enter full name")}
                            value={formData.fullName}
                            onChange={(v) => handleChange("fullName", v)}
                            icon={<User size={16} className="text-neutral-500 dark:text-neutral-400" />}
                          />
                          <ErrorText>{errors.fullName}</ErrorText>
                        </div>

                        {/* Company Name */}
                        <div className="space-y-1">
                          <InputCustom
                            label={t("Company Name")}
                            placeholder={t("Enter company name")}
                            value={formData.companyName}
                            onChange={(v) => handleChange("companyName", v)}
                            icon={<Building size={16} className="text-neutral-500 dark:text-neutral-400" />}
                          />
                          <ErrorText>{errors.companyName}</ErrorText>
                        </div>

                        {/* WhatsApp Number */}
                        <div className="space-y-1">
                          <InputCustom
                            label={t("Whatsapp Number")}
                            type="tel"
                            placeholder={t("Enter phone whatsapp number")}
                            value={formData.phone}
                            onChange={(v) => handleChange("phone", v)}
                            icon={<Phone size={16} className="text-neutral-500 dark:text-neutral-400" />}
                          />
                          <ErrorText>{errors.phone}</ErrorText>
                        </div>

                        {/* Telegram Username */}
                        <div className="space-y-1">
                          <InputCustom
                            label={t("Telegram Username")}
                            type="text"
                            placeholder={t("Enter your Telegram username")}
                            value={formData.telegram}
                            onChange={(v) => handleChange("telegram", v)}
                            icon={<AtSign size={16} className="text-neutral-500 dark:text-neutral-400" />}
                          />
                        </div>

                        {/* Email */}
                        <div className="space-y-1">
                          <InputCustom
                            label={t("Email Address")}
                            type="email"
                            placeholder={t("Enter email address")}
                            value={formData.email}
                            onChange={(v) => handleChange("email", v)}
                            icon={<Mail size={16} className="text-neutral-500 dark:text-neutral-400" />}
                          />
                          <ErrorText>{errors.email}</ErrorText>
                        </div>

                        {/* Platform */}
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            {t("Platform")}
                          </label>
                          <SearchableSelect
                            options={platformOptions}
                            value={formData.platform}
                            onChange={(v) => handleChange("platform", v)}
                            placeholder={t("Select platform")}
                            icon={<Globe size={16} />}
                          />
                          <ErrorText>{errors.platform}</ErrorText>
                        </div>

                        {/* Monthly Spend */}
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            {t("Monthly Spend")}
                          </label>
                          {(() => {
                            const current = formData.monthlySpend;
                            const isKnown = baseMonthlySpendOptions.some((o) => o.value === current);
                            const monthlySpendOptions =
                              current && !isKnown
                                ? [
                                    { id: `custom-${current}`, name: current, value: current },
                                    ...baseMonthlySpendOptions,
                                  ]
                                : baseMonthlySpendOptions;
                            return (
                              <SearchableSelect
                                options={monthlySpendOptions}
                                value={current}
                                onChange={(v) => handleChange("monthlySpend", v)}
                                placeholder={t("Select monthly spend")}
                                icon={<DollarSign size={16} />}
                              />
                            );
                          })()}
                          <ErrorText>{errors.monthlySpend}</ErrorText>
                        </div>

                        {/* Category */}
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            {t("Category")}
                          </label>
                          <SearchableSelect
                            options={categoryOptions}
                            value={categoryChoice}
                            onChange={handleCategoryChoice}
                            placeholder={t("Select category")}
                            icon={<Tag size={16} />}
                          />
                          {categoryChoice === "Other" && (
                            <InputCustom
                              placeholder={t("Enter custom category")}
                              value={customCategory}
                              onChange={handleCustomCategoryChange}
                              icon={<Tag size={16} className="text-neutral-500 dark:text-neutral-400" />}
                            />
                          )}
                          <ErrorText>{errors.category}</ErrorText>
                        </div>

                        {/* Store URL */}
                        <div className="space-y-1">
                          <InputCustom
                            label={t("Website")}
                            type="text"
                            placeholder={t("Enter website url")}
                            value={formData.store}
                            onChange={(v) => handleChange("store", v)}
                            icon={<Globe2 size={16} className="text-neutral-500 dark:text-neutral-400" />}
                          />
                        </div>

                        {/* Status */}
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            {t("Status")}
                          </label>
                          <SearchableSelect
                            options={statusOptions}
                            value={formData.status}
                            onChange={(v) => handleChange("status", v)}
                            placeholder={t("Select status")}
                            icon={<AlertCircle size={16} />}
                          />
                        </div>

                        {/* Join Date */}
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            {t("Join Date")}
                          </label>
                          <DateTimePicker
                            value={formData.joinDate}
                            onChange={(v) => handleChange("joinDate", v)}
                            placeholder={t("Select join date & time")}
                          />
                        </div>

                        {/* Notes */}
                        <div className="space-y-1 md:col-span-2">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            {t("Notes")}
                          </label>
                          <textarea
                            value={formData.notes}
                            onChange={(e) => handleChange("notes", e.target.value)}
                            placeholder={t("Add any additional notes about this lead")}
                            rows={3}
                            className="w-full px-3 py-2 text-sm border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      {/* Info box */}
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertCircle size={16} className="text-blue-600 dark:text-blue-400" />
                          <h3 className="text-sm font-medium text-blue-700 dark:text-blue-400">
                            {t("Lead Information")}
                          </h3>
                        </div>
                        <p className="text-xs text-blue-600 dark:text-blue-300">
                          {leadId
                            ? t("Update the lead information. You can change their status as they progress through your sales pipeline.")
                            : t("New leads are automatically assigned a 'New' status. You can update their status later as they progress.")}
                        </p>
                      </div>
                    </form>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-xl hover:bg-neutral-50 dark:bg-transparent dark:text-neutral-300 dark:border-neutral-600 dark:hover:bg-neutral-800 focus:outline-none"
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={processing || loading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("Processing...")}
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {leadId ? t("Update Lead") : t("Save Lead")}
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default LeadFormModal;
